// @flow
import React, { Component, Fragment } from "react";
import { withGlobalContext } from "../../common/GlobalContext";
import Downshift from "downshift";
import withStyles from "elevate-ui/withStyles";
import Button from "elevate-ui/Button";
import Alert from "elevate-ui/Alert";
import Typography from "elevate-ui/Typography";
import LocationCity from "elevate-ui-icons/LocationCity";
import ConfirmationNumber from "elevate-ui-icons/ConfirmationNumber";
import LocationSearching from "elevate-ui-icons/LocationSearching";
import LocationOn from "elevate-ui-icons/LocationOn";
import FilterList from "elevate-ui-icons/FilterList";
import SearchIcon from "elevate-ui-icons/Search";
import axios from "axios";
import classNames from "classnames";
import debounce from "lodash/debounce";
import querystring from "querystring";
import _ from "lodash";
import Filters from "./Filters";

type $Props = {
  classes: Object,
  isRental: boolean,
  initialFilterState?: string,
  showColorButtons: boolean,
  filterBackground?: string,
  filterPosition?: string,
  theme: Object,
  hideFilterButton?: boolean,
  compact?: boolean,
  mlsApiVersion: number,
  isCanadian: boolean,
};

type $State = {
  value: string,
  items: Array<Object>,
  params: Object,
  filtersOpen: boolean,
  loading: boolean,
  error: boolean,
};

class HeaderSearch extends Component<$Props, $State> {
  constructor(props) {
    super(props);
    this.state = {
      value: "",
      items: [],
      params: {
        cities: null,
        zipCode: null,
        type: null,
        minPrice: null,
        maxPrice: null,
        minBeds: null,
        minBaths: null,
        isRental: props.isRental || false,
      },
      filtersOpen: props.initialFilterState === "open" ? true : false,
      loading: false,
      error: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.isRental !== prevProps.isRental) {
      const { params } = this.state;
      this.setState({
        params: {
          ...params,
          isRental: this.props.isRental,
        },
      });
    }
  }

  quickSearch: any;

  generateIcon = (type) => {
    const Icons = {
      cities: LocationCity,
      mls: ConfirmationNumber,
      zipCode: LocationSearching,
      address: LocationOn,
    };

    const { classes } = this.props;

    const Element = Icons[type];

    return <Element className={classes.itemIcons} size={26} />;
  };

  transformSuggestions = (suggestions) => {
    const dictionary = {
      cities: {
        label: "City",
        childId: this.props.mlsApiVersion === 1 ? "city" : "cityId",
        childItem: "cityState",
        type: "cities",
      },
      zipCodes: {
        label: "Zip Code",
        childId: "zipCode",
        childItem: "zipCode",
        type: "zipCode",
      },
      listingsByAddress: {
        label: "Address",
        childId: "id",
        childItem: "address",
        type: "address",
      },
      listingsByMlsListingId: {
        label: "MLS #",
        childId: "id",
        childItem: "mlsListingId",
        type: "mls",
      },
    };

    const results = Object.keys(suggestions).map((suggestion) => {
      return {
        text: dictionary[suggestion].label,
        type: dictionary[suggestion].type,
        children: suggestions[suggestion].map((child) => {
          return {
            id: child[dictionary[suggestion].childId],
            text: child[dictionary[suggestion].childItem],
            type: dictionary[suggestion].type,
          };
        }),
      };
    });
    return results;
  };

  /**
   * Handle user input
   **/
  handleInput = (inputValue) => {
    if (inputValue === "" || (inputValue && inputValue.length < 3)) {
      return null;
    } else {
      this.debounceSearch(inputValue);
    }
  };

  /**
   * Adds params from quicksearch when selected
   **/
  handleParams = (selection) => {
    const { params } = this.state;
    this.quickSearch.value = selection.text;
    this.setState({
      params: {
        ...params,
        [selection.type]: selection.id,
      },
    });
  };

  /**
   * Handle when a selection is made from the dropdown
   **/
  handleChange = (selection) => {
    switch (selection.type) {
      case "address":
        return (window.location = `/listings/${selection.id}`);
      case "mls":
        return (window.location = `/listings/${selection.id}`);
      default:
        return this.handleParams(selection);
    }
  };

  /**
   * Handle results from quicksearch api
   **/
  handleQuickSearch = (inputValue) => {
    return axios
      .get("api/quicksearch", {
        params: {
          searchText: inputValue,
        },
      })
      .then((res) => {
        return this.transformSuggestions(res.data.suggestions);
      })
      .then((results) => this.setState({ items: results }))
      .then(() => this.setState({ loading: false }))
      .catch(() => {
        this.setState({ error: true, loading: false });
      });
  };

  /**
   * Waits for user to stop typing before sending requests
   **/
  debounceSearch = debounce((inputValue) => {
    this.setState({ loading: true });
    this.handleQuickSearch(inputValue);
  }, 300);

  /**
   * Handle when a user clicks the search icon
   **/
  handleSearch = () => {
    let { params } = this.state;
    if (this.props.mlsApiVersion === 2) {
      params = Object.assign({}, _.omit(params, Object.keys(params)), {
        a_cityID: params.cities,
        p_postalCode: params.zipCode,
        amin_listPrice: params.minPrice,
        amax_listPrice: params.maxPrice,
        pmin_bedroomsTotal: params.minBeds,
        pmin_bathroomsFull: params.minBaths,
        p_propertyType: params.type,
        p_rentalYN: params.isRental,
      });
    }
    const query = querystring.stringify(params);
    return (window.location = `/listings?${query}`);
  };

  /**
   * Toggle extra filters open/closed
   **/
  toggleFilters = () => {
    this.setState((state) => ({ filtersOpen: !state.filtersOpen }));
  };

  /**
   * Handle when property type filters are clicked
   **/
  handlePropertyTypes = (e) => {
    const {
      params,
      params: { type },
    } = this.state;
    if (!type) {
      params.type = e.target.value;
      this.setState({
        params: { ...params },
      });
    } else {
      const updatedType = [type];
      updatedType.push(e.target.value);
      params.type = updatedType;
      this.setState({
        params: { ...params },
      });
    }
  };

  /**
   * Handle price changes in filter
   **/
  handleSelectChange = (event) => {
    const { params } = this.state;
    const updatedParams = params;

    updatedParams[event.target.id] = event.target.value;

    this.setState({
      params: {
        ...updatedParams,
      },
    });
  };

  render() {
    const {
      classes,
      filterBackground,
      filterPosition,
      hideFilterButton,
      initialFilterState,
      showColorButtons,
      theme,
      compact,
      isCanadian,
    } = this.props;
    const { error, items, filtersOpen, loading } = this.state;

    return (
      <div className={classes.searchBackground}>
        {error && (
          <div className={classes.errorContainer}>
            <Alert color="error">
              <Typography type="body" style={{ marginBottom: 0 }}>
                Something went wrong. Please try again later.
              </Typography>
            </Alert>
          </div>
        )}
        <Downshift
          onChange={(selection) => this.handleChange(selection)}
          onInputValueChange={(inputValue) => this.handleInput(inputValue)}
          itemToString={(item) => (item ? item.value : "")}
        >
          {({
            getInputProps,
            getItemProps,
            getLabelProps,
            isOpen,
            highlightedIndex,
            selectedItem,
          }) => {
            let index = 0;
            const placeholderText = isCanadian
              ? "City, Postal, Address, or MLS #"
              : "City, Zip, Address, or MLS #";
            return (
              <div className={classes.root}>
                <label className={classes.label} {...getLabelProps()}>
                  Search For Homes
                </label>
                <div className={classes.inputContainer}>
                  <input
                    className={classes.searchInput}
                    {...getInputProps()}
                    ref={(node) => (this.quickSearch = node)}
                    placeholder={placeholderText}
                  />
                  <div
                    className={classNames({
                      [classes.loadingIndicator]: true,
                      [classes.loading]: loading,
                    })}
                  >
                    <svg
                      version="1.1"
                      id="loader-1"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlnsXlink="http://www.w3.org/1999/xlink"
                      x="0px"
                      y="0px"
                      width="40px"
                      height="40px"
                      viewBox="0 0 40 40"
                      enableBackground="new 0 0 40 40"
                      xmlSpace="preserve"
                    >
                      <path
                        opacity="0.2"
                        fill="#2E2E35"
                        d="M20.201,5.169c-8.254,0-14.946,6.692-14.946,14.946c0,8.255,6.692,14.946,14.946,14.946
    s14.946-6.691,14.946-14.946C35.146,11.861,28.455,5.169,20.201,5.169z M20.201,31.749c-6.425,0-11.634-5.208-11.634-11.634
    c0-6.425,5.209-11.634,11.634-11.634c6.425,0,11.633,5.209,11.633,11.634C31.834,26.541,26.626,31.749,20.201,31.749z"
                      />
                      <path
                        fill="#000"
                        d="M26.013,10.047l1.654-2.866c-2.198-1.272-4.743-2.012-7.466-2.012h0v3.312h0
    C22.32,8.481,24.301,9.057,26.013,10.047z"
                      >
                        <animateTransform
                          attributeType="xml"
                          attributeName="transform"
                          type="rotate"
                          from="0 20 20"
                          to="360 20 20"
                          dur="0.5s"
                          repeatCount="indefinite"
                        />
                      </path>
                    </svg>
                  </div>
                  {!hideFilterButton && (
                    <Button
                      type="button"
                      color={showColorButtons ? "#5cb85c" : "#DDDDDD"}
                      className={classes.filterButton}
                      title="Filter"
                      onClick={() => this.toggleFilters()}
                    >
                      <FilterList
                        color={showColorButtons ? "#FFFFFF" : "#000000"}
                        size={24}
                      />
                    </Button>
                  )}
                  <Button
                    type="button"
                    color={showColorButtons ? "#337ab7" : "#DDDDDD"}
                    className={classes.searchButton}
                    title="Search"
                    onClick={() => this.handleSearch()}
                  >
                    <SearchIcon size={24} />
                  </Button>
                </div>
                {isOpen ? (
                  <div className={classes.dropdown}>
                    {items.map((item) => {
                      return (
                        <Fragment key={item.text}>
                          <div
                            key={item.text}
                            className={classNames(
                              classes.item,
                              classes.itemHeader
                            )}
                          >
                            {this.generateIcon(item.type)}
                            {item.text}
                          </div>
                          {item.children.map((childItem) => {
                            index++;
                            return (
                              <div
                                key={childItem.id + index}
                                className={classes.item}
                                {...getItemProps({
                                  key: childItem.id,
                                  index,
                                  item: childItem,
                                  style: {
                                    backgroundColor:
                                      highlightedIndex === index
                                        ? theme.colors.gray["100"]
                                        : "white",
                                    fontWeight:
                                      selectedItem === childItem
                                        ? "bold"
                                        : "normal",
                                  },
                                })}
                              >
                                {childItem.text}
                              </div>
                            );
                          })}
                        </Fragment>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          }}
        </Downshift>
        <Filters
          filterBackground={filterBackground}
          filterPosition={filterPosition}
          filtersOpen={filtersOpen}
          handlePropertyTypes={this.handlePropertyTypes}
          handleSelectChange={this.handleSelectChange}
          initialFilterState={initialFilterState}
          compact={compact}
        />
      </div>
    );
  }
}

export default withGlobalContext(
  withStyles((theme) => ({
    searchBackground: {
      width: "100%",
    },
    root: {
      position: "relative",
      width: "100%",
    },
    inputContainer: {
      display: "flex",
      flex: "1 1 100%",
      position: "relative",
      flexFlow: "row nowrap",
      justifyContent: "flex-start",
      alignItems: "center",
      width: "100%",
    },
    searchInput: {
      display: "inline-block",
      width: "100%",
      height: "62px",
      color: theme.typography.bodyColor,
      fontFamily: "inherit",
      fontWeight: "400",
      fontSize: "15px",
      lineHeight: "25px",
      backgroundColor: theme.colors.white,
      border: `1px solid ${theme.colors.gray["300"]}`,
      borderRadius: "0",
      borderRight: "none",
      padding: "8px 12px",
      boxShadow: "none", // Reset default inputs for mozilla
      "-webkit-appearance": "none", // Reset default browser styles
      "-moz-appearance": "none", // Reset default browser styles

      "&:focus": {
        outline: "none", // Disable default focus glow
        boxShadow: theme.globalBoxShadow, // Add back focus style
      },
      "&:disabled": {
        cursor: "not-allowed",
      },
      [theme.breakpoints(480)]: {
        fontSize: (props) => (props.compact ? "14px" : "18px"),
      },
    },
    filterButton: {
      display: "none",
      [theme.breakpoints(900)]: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "55px",
        height: "62px",
        borderRadius: "0",
        appearance: "none",
        "-webkit-appearance": "none",
        "-moz-appearance": "none",
      },
    },
    searchButton: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "55px",
      height: "62px",
      borderRadius: "0",
      borderTopRightRadius: "8px",
      borderBottomRightRadius: "8px",
    },
    dropdown: {
      position: "absolute",
      width: (props) =>
        props.compact ? "calc(100% - 48px)" : "calc(100% - 97px)",
      maxHeight: (props) => (props.compact ? "250px" : ""),
      overflowY: "scroll",
      color: theme.typography.bodyColor,
      fontFamily: "inherit",
      fontWeight: "400",
      fontSize: "16px",
      lineHeight: "20px",
      backgroundColor: theme.colors.white,
      border: `1px solid ${theme.colors.gray["300"]}`,
      borderTop: "none",
      boxShadow: "none",
      zIndex: theme.zIndex.dropdown + 1,
    },
    item: {
      padding: "8px 12px",
    },
    itemHeader: {
      fontWeight: "bold",
    },
    itemIcons: {
      display: "inlineBlock",
      verticalAlign: "middle",
      margin: "0 8px 4px 0",
    },
    label: { display: "none" },
    loadingIndicator: {
      display: "none",
      position: "absolute",
      right: "110px",
    },
    loading: { display: "flex" },
    errorContainer: {
      marginBottom: theme.spacing.unit * 0.75,
    },
  }))(HeaderSearch)
);
